"use client";

import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import { useEffect, useState } from "react";
import { PhoneFrame } from "@/components/shell/AppShell";
import { api } from "@/components/client";

function loginError(code: string | null) {
  if (code === "exists") return "같은 이메일의 계정이 이미 있어요. 이메일로 로그인해 주세요.";
  if (code === "config") return "Google 로그인은 공급자 설정 후 사용할 수 있어요.";
  if (code === "google") return "Google 로그인을 마치지 못했어요. 다시 시도해 주세요.";
  return "";
}

export function LoginForm() {
  const router = useRouter();
  const params = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(loginError(params.get("error")));

  return (
    <PhoneFrame center wordmark>
      <form
        className="stack"
        onSubmit={async (event) => {
          event.preventDefault();
          setError("");
          try {
            await api("/api/auth/session", { method: "PUT", body: JSON.stringify({ email, password }) });
            router.push("/");
            router.refresh();
          } catch (reason) {
            setError(reason instanceof Error ? reason.message : "로그인하지 못했어요");
          }
        }}
      >
        <label>
          이메일
          <input value={email} onChange={(event) => setEmail(event.target.value)} type="email" required autoComplete="email" />
        </label>
        <label>
          비밀번호
          <input value={password} onChange={(event) => setPassword(event.target.value)} type="password" required autoComplete="current-password" />
        </label>
        {error && <p className="error">{error}</p>}
        <button className="primary" type="submit">로그인</button>
        <a className="primary" href="/api/auth/google/start">Google로 계속</a>
        <p className="auth-links"><Link href="/signup">회원가입</Link> · <Link href="/reset">비밀번호 재설정</Link></p>
      </form>
    </PhoneFrame>
  );
}

export function SignupForm() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [verifyPath, setVerifyPath] = useState<string | null>(null);
  const [error, setError] = useState("");
  return (
    <PhoneFrame>
      <form
        className="stack"
        onSubmit={async (event) => {
          event.preventDefault();
          setError("");
          try {
            const result = await api<{ verifyPath: string | null }>("/api/auth/session", { method: "POST", body: JSON.stringify({ email, password }) });
            setVerifyPath(result.verifyPath);
          } catch (reason) {
            setError(reason instanceof Error ? reason.message : "가입하지 못했어요");
          }
        }}
      >
        <h1>회원가입</h1>
        <label>이메일<input value={email} onChange={(event) => setEmail(event.target.value)} type="email" required /></label>
        <label>비밀번호<input value={password} onChange={(event) => setPassword(event.target.value)} type="password" required minLength={8} /></label>
        {error && <p className="error">{error}</p>}
        <button className="primary" type="submit">인증 메일 만들기</button>
        {verifyPath && <p>메일을 보낼 수 없는 개발 환경이에요. <Link href={verifyPath}>인증 링크</Link>로 계속해 주세요.</p>}
        <p><Link href="/login">로그인으로</Link></p>
      </form>
    </PhoneFrame>
  );
}

export function ResetForm({ token }: { token: string }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [path, setPath] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  return (
    <PhoneFrame>
      <form
        className="stack"
        onSubmit={async (event) => {
          event.preventDefault();
          setError("");
          try {
            if (!token) {
              const result = await api<{ resetPath: string | null }>("/api/auth/recovery", { method: "POST", body: JSON.stringify({ purpose: "reset-request", email }) });
              setPath(result.resetPath);
              setDone(true);
              return;
            }
            await api("/api/auth/recovery", { method: "POST", body: JSON.stringify({ purpose: "reset", token, password }) });
            setDone(true);
          } catch (reason) {
            setError(reason instanceof Error ? reason.message : "재설정하지 못했어요");
          }
        }}
      >
        <h1>비밀번호 재설정</h1>
        {!token && <label>이메일<input value={email} onChange={(event) => setEmail(event.target.value)} type="email" required /></label>}
        {token && <label>새 비밀번호<input value={password} onChange={(event) => setPassword(event.target.value)} type="password" required minLength={8} /></label>}
        {error && <p className="error">{error}</p>}
        <button className="primary" type="submit">{token ? "비밀번호 바꾸기" : "재설정 링크 만들기"}</button>
        {done && !token && <p>계정이 있으면 링크를 준비했어요. {path && <Link href={path}>재설정 계속</Link>}</p>}
        {done && token && <p><Link href="/login">로그인하기</Link></p>}
      </form>
    </PhoneFrame>
  );
}

export function VerifyForm({ token }: { token: string }) {
  const [message, setMessage] = useState("인증을 확인하고 있어요");
  useEffect(() => {
    let stop = false;
    void api("/api/auth/recovery", { method: "POST", body: JSON.stringify({ purpose: "verify", token }) })
      .then(() => {
        if (!stop) setMessage("인증을 마쳤어요.");
      })
      .catch((reason) => {
        if (!stop) setMessage(reason instanceof Error ? reason.message : "인증하지 못했어요");
      });
    return () => {
      stop = true;
    };
  }, [token]);
  return (
    <PhoneFrame>
      <div className="stack">
        <h1>이메일 인증</h1>
        <p>{message}</p>
        <Link href="/login">로그인</Link>
      </div>
    </PhoneFrame>
  );
}
